import {
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  numberAttribute,
  OnChanges,
  OnDestroy,
  SimpleChange,
  ViewEncapsulation,
} from '@angular/core';
import { BehaviorSubject, Observable, Subscription, timer } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { PercentageContext } from './percentage-options';

@Component({
  selector: 'kz-percentage',
  template: `<div class="center">
                <span class="value" *ngIf="displayLabel" [style.font-size]="(this.radius/20) + 'rem'">{{cpt | async}}%</span>
                <ng-content></ng-content>
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" id="xs-chart" class="xs-chart" x="0px" y="0px" [attr.width]="boxSize" [attr.height]="boxSize" [attr.viewBox]="'0 0 '+boxSize+' '+boxSize"   xml:space="preserve">
                <circle class="chart-circle first-circle" [attr.cx]="boxSize/2" [attr.cy]="boxSize/2" [attr.r]="radius" [ngStyle]="{'stroke-width': strokeWidth}"/>
                <circle class="chart-circle second-circle" [attr.cx]="boxSize/2" [attr.cy]="boxSize/2" [attr.r]="radius" [ngStyle]="{'stroke-dashoffset': (pourcentStrokeDashoffset | async), 'stroke-dasharray': strokeDasharray , 'stroke-width': strokeWidth}" />
              </svg>`,
  styleUrls: ['./kz-percentage.component.scss'],
  host: {
    '[class]': '"kz-percentage" + (classes ? " " + classes : "")',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class KzPercentageComponent implements OnDestroy, OnChanges {

  @Input({transform: numberAttribute})
  pourcent = 0;

  @Input({transform: numberAttribute})
  radius = 40;

  @Input({transform: numberAttribute})
  strokeWidth = 3;

  @Input({transform: booleanAttribute})
  displayLabel = true;

  @Input({transform: booleanAttribute})
  animateAtFirst = true;

  private _classes: string;

  get classes(): string {
    return this._classes;
  }

  private _percentageContext: PercentageContext = 'default';

  get percentageContext(): PercentageContext {
    return this._percentageContext;
  }

  @Input()
  set percentageContext(value: PercentageContext) {
    this._percentageContext = value;
  }


  public _cpt: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  private sub: Subscription;

  constructor(private _changeDetectorRef: ChangeDetectorRef) {
  }

  get boxSize(): number {
    return 2 * this.radius + this.strokeWidth;
  }

  get strokeDasharray(): number {
    return 2 * this.radius * Math.PI;
  }

  get pourcentStrokeDashoffset(): Observable<number> {
    return this.cpt.pipe(map((x) => this.strokeDasharray - (x / 100 * this.strokeDasharray)));
  }

  public get cpt(): Observable<number> {
    return this._cpt.asObservable();
  }

  ngOnChanges(changes: { [propName: string]: SimpleChange }) {
    for (const propName in changes) {
      if (propName === 'pourcent') {
        const itemChange: SimpleChange = changes.pourcent;
        if (!this.animateAtFirst && (!itemChange || itemChange.isFirstChange())) {
          this._cpt.next(itemChange.currentValue);
          return;
        }

        const currentPourcent = itemChange.currentValue;
        const previousPourcent = itemChange.previousValue || 0;
        const nb = Math.abs(currentPourcent - previousPourcent);
        const increment = (currentPourcent > previousPourcent) ? 1 : -1;

        if (this.sub) {
          this.sub.unsubscribe();
        }
        this.sub = timer(0, (500 / nb)).pipe(
            map((i) => previousPourcent + (i * increment)),
            take(nb + 1),
            tap((x) => this._cpt.next(x))
        ).subscribe();
      } else if (propName === 'displayLabel') {
        this._changeDetectorRef.detectChanges();
      }
      this._updateClasses();
      this._changeDetectorRef.detectChanges();
    }
  }

  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  ngOnInit(): void {
    this._updateClasses();
    this._changeDetectorRef.detectChanges();
  }

  private _updateClasses() {
    this._classes = '';

    if (this.percentageContext && this.percentageContext !== 'default') {
      this._classes += ` kz-percentage--${ this.percentageContext }`;
    }

  }
}
